import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Public screenshot-upload page opened from the reward-task WA template.
// Route: /task-upload/:token

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND = {
  cream: '#F5F2EA',
  charcoal: '#1A1A1A',
  slate: '#4A4A4A',
  gold: '#C5A059',
  goldSoft: 'rgba(197, 160, 89, 0.25)',
  emerald: '#059669',
  emeraldDeep: '#047857',
};
const fontHead = { fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontWeight: 300, letterSpacing: '-0.01em', color: BRAND.charcoal };
const fontBody = { fontFamily: "'Manrope', 'Inter', system-ui, sans-serif", color: BRAND.charcoal };
const overlineStyle = { fontFamily: "'Manrope', system-ui, sans-serif", fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', color: BRAND.gold };

const TaskUploadPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&family=Manrope:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    document.body.style.backgroundColor = BRAND.cream;
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/task-upload/${encodeURIComponent(token)}`);
        setInfo(res.data);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Could not load this task');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(null);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/api/public/task-upload/${encodeURIComponent(token)}`, fd);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed — please try again');
    } finally {
      setUploading(false);
    }
  };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: BRAND.cream, ...fontBody, padding: '72px 24px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }} data-testid="task-upload-page">
        <div style={overlineStyle}>Eden Skin Clinic · Reward Task</div>
        {children}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: `1px solid ${BRAND.goldSoft}` }}>
          <p style={{ color: BRAND.slate, fontSize: 13 }}>
            Need help? Call us at{' '}
            <a href="tel:+917710117777" style={{ color: BRAND.emerald, textDecoration: 'none', fontWeight: 600 }}>+91 77101 17777</a>
          </p>
        </div>
      </div>
    </div>
  );

  if (loading) return wrap(<h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }}>Loading…</h1>);

  if (err && !info) {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="task-upload-error">Link unavailable</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>{err}</p>
      </>
    );
  }

  if (info?.closed) {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }}>Task already reviewed</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
          This task has already been reviewed by our team. Thank you{info.patient_first_name ? `, ${info.patient_first_name}` : ''}!
        </p>
      </>
    );
  }

  if (done) {
    return wrap(
      <>
        <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }} data-testid="task-upload-success">Screenshot received!</h1>
        <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
          Thank you{info?.patient_first_name ? `, ${info.patient_first_name}` : ''}. Our team will verify your
          submission shortly — once approved, your discount voucher will arrive on WhatsApp.
        </p>
      </>
    );
  }

  return wrap(
    <>
      <h1 style={{ ...fontHead, fontSize: 42, margin: '16px 0' }}>
        Hi{info?.patient_first_name ? ` ${info.patient_first_name}` : ''}, upload your screenshot
      </h1>
      <p style={{ color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>
        Completed <strong>{info?.task_name}</strong>? Upload a screenshot below as proof.
        Once verified, we'll send your discount voucher on WhatsApp.
      </p>
      {info?.uploaded && (
        <p style={{ color: BRAND.emeraldDeep, fontSize: 14, marginTop: 12, fontWeight: 600 }}>
          A screenshot was already uploaded and is being reviewed. Uploading again will replace it.
        </p>
      )}
      {err && <p style={{ color: '#B91C1C', fontSize: 14, marginTop: 12 }} data-testid="task-upload-inline-error">{err}</p>}

      <div style={{ marginTop: 24, padding: 24, background: '#FFFFFF', border: `1px solid ${BRAND.goldSoft}` }}>
        <input
          type="file"
          accept="image/*"
          onChange={onPick}
          data-testid="task-upload-file-input"
          style={{ ...fontBody, fontSize: 14, width: '100%' }}
        />
        {preview && (
          <img src={preview} alt="preview" style={{ marginTop: 16, maxWidth: '100%', maxHeight: 320, border: `1px solid ${BRAND.goldSoft}` }} />
        )}
        <button
          onClick={onUpload}
          disabled={!file || uploading}
          data-testid="task-upload-submit-button"
          style={{
            marginTop: 20, width: '100%', padding: '14px 24px', border: 'none', cursor: (!file || uploading) ? 'not-allowed' : 'pointer',
            background: (!file || uploading) ? '#9CA3AF' : BRAND.emerald, color: '#fff',
            ...fontBody, fontWeight: 700, fontSize: 15, letterSpacing: '0.05em', textTransform: 'uppercase',
          }}
        >
          {uploading ? 'Uploading…' : 'Submit screenshot'}
        </button>
      </div>
    </>
  );
};

export default TaskUploadPage;
